import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';

import { ProcessPermissionsDTO, useGetProcessPermissions } from '@/api/generated';
import { useProcess } from '@/hooks/entities/useProcess';
import { getErrorStatus } from '@/utils/error';

import { PermissionContextVariables } from './usePermissions';

type UseProcessPermissionsResult = {
  value?: ProcessPermissionsDTO;
  error: unknown;
  isLoading: boolean;
  isAccessDenied: boolean;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<ProcessPermissionsDTO, unknown>> | undefined;
};

export const useProcessPermissions = ({
  variables
}: {
  variables?: PermissionContextVariables;
} = {}): UseProcessPermissionsResult => {
  const process = useProcess();

  const processId = variables?.processId ?? process.processId;

  const {
    data: processPermissions,
    error,
    isLoading,
    refetch
  } = useGetProcessPermissions(processId!, {
    query: { enabled: !!processId }
  });

  const isAccessDenied = useMemo(() => [401, 403, 404].includes(getErrorStatus(error) ?? 0), [error]);

  const canReturnValue = !!processId && !isAccessDenied;

  const canRefetchValueRef = useRef(canReturnValue);

  useEffect(() => {
    canRefetchValueRef.current = canReturnValue;
  }, [canReturnValue]);

  const refetchWithCanReturnValue = useCallback(
    <TPageData>(options?: RefetchOptions & RefetchQueryFilters<TPageData>) => {
      if (canRefetchValueRef.current) {
        return refetch(options);
      }
    },
    [refetch]
  );

  return {
    value: canReturnValue ? processPermissions || {} : undefined,
    error,
    isLoading,
    isAccessDenied,
    refetch: refetchWithCanReturnValue
  };
};
